import { useEffect, useState } from "react";

import { PolicyDocument } from "./components/PolicyDocument";
import "./styles/public-policy.css";

import { activeFormattingLocale } from "./i18n/formatting";
type PublicPolicyType = "privacy_policy" | "terms_of_service";

type PublicPolicy = {
  policy_type: PublicPolicyType;
  title: string;
  version: number;
  body: string;
  published_at: string;
  effective_at: string;
  last_updated: string;
};

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "Unavailable";
  return new Intl.DateTimeFormat(activeFormattingLocale(), { dateStyle: "long" }).format(date);
}

export function PublicPolicyPage({
  kind,
  onNavigatePolicy,
  onBack,
}: {
  kind: "privacy" | "terms";
  onNavigatePolicy: (kind: "privacy" | "terms") => void;
  onBack: () => void;
}) {
  const policyType: PublicPolicyType = kind === "privacy" ? "privacy_policy" : "terms_of_service";
  const [document, setDocument] = useState<PublicPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setUnavailable(false);
    setDocument(null);
    fetch(`${API_BASE}/public/policies/${policyType}`, {
      headers: { Accept: "application/json" },
      credentials: "omit",
    })
      .then(async (response) => {
        if (response.status === 404) return null;
        if (!response.ok) throw new Error("policy unavailable");
        return response.json() as Promise<PublicPolicy>;
      })
      .then((value) => {
        if (!active) return;
        if (value) setDocument(value);
        else setUnavailable(true);
      })
      .catch(() => { if (active) setUnavailable(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [policyType]);

  return <main className="public-policy-page">
    <section className="public-policy-hero">
      <div className="shell">
        <span className="eyebrow light">KAVOOSH POLICY</span>
        <h1>{document?.title ?? (kind === "privacy" ? "Privacy notice" : "Terms of service")}</h1>
        {document ? <p className="public-policy-updated">Last updated {formatDate(document.last_updated)}</p> : <p className="public-policy-updated">Authoritative publication status</p>}
        <nav aria-label="Public legal documents">
          <button className={kind === "privacy" ? "active" : ""} onClick={() => onNavigatePolicy("privacy")}>Privacy notice</button>
          <button className={kind === "terms" ? "active" : ""} onClick={() => onNavigatePolicy("terms")}>Terms of service</button>
        </nav>
      </div>
    </section>
    <section className="shell public-policy-layout">
      {loading && <div className="public-policy-state" role="status"><strong>Loading published policy…</strong><p>Kavoosh is resolving the current authoritative version.</p></div>}
      {!loading && unavailable && <div className="public-policy-state" role="status"><strong>No published version is available yet.</strong><p>Draft Admin content is never shown on the public website. Please check again after an authorized version is published.</p><button className="button secondary" onClick={onBack}>Return to Kavoosh</button></div>}
      {!loading && document && <article className="public-policy-paper">
        <header><div><span>Published version</span><strong>v{document.version}</strong></div><div><span>Last updated</span><strong>{formatDate(document.last_updated)}</strong></div></header>
        <PolicyDocument body={document.body} />
      </article>}
    </section>
  </main>;
}
